/**
 * What the login form says when a sign-in attempt fails.
 *
 * Pure, and separate from the page, because the copy here is a security
 * decision as much as a UX one: every credential failure — unknown email, wrong
 * password, an account that has no password set yet — must produce ONE string.
 * The moment they diverge, an anonymous caller can enumerate who has an account
 * on this portal. Keeping the mapping in one testable function is what stops a
 * later "helpful" branch ("no account with that email") from being added
 * without anyone noticing what it gives away.
 *
 * The three transport strings are imported, not retyped — see the note on them
 * in AuthContext.
 */
import { ApiCallError } from './api';
import {
  NETWORK_MESSAGE,
  SERVER_ERROR_MESSAGE,
  TIMEOUT_MESSAGE,
  UNKNOWN_ERROR_MESSAGE,
} from './AuthContext';
import { DEACTIVATED_NOTICE } from './authNotice';

/** The one string for every credential failure. */
export const INVALID_CREDENTIALS_MESSAGE = 'Email or password is incorrect.';

/**
 * Kinds the page renders differently. `throttled` is the only one that isn't a
 * finished sentence — it carries a live countdown and an inline reset link, so
 * the page composes it.
 */
export type LoginErrorKind = 'credentials' | 'deactivated' | 'throttled' | 'transport';

export interface LoginError {
  kind: LoginErrorKind;
  /** Complete copy for every kind except `throttled`, where it is the fallback. */
  message: string;
  /** Seconds until another attempt is allowed. Only ever set for `throttled`. */
  retryAfterSeconds: number | null;
}

/**
 * Server backoff tops out at 900s (§1) and the edge window is 15 minutes, so an
 * hour is well past anything real — anything larger is a bad payload, not a
 * lockout, and rendering "try again in 8 hours" would send Joey to the phone.
 */
const MAX_RETRY_SECONDS = 3600;

/** Used when a 429 arrives without a usable hint. Being short is safe: the worst
 *  case is one more 429, which re-arms the countdown with the real number. */
const DEFAULT_RETRY_SECONDS = 60;

function retryAfterFrom(payload: Record<string, unknown>): number | null {
  // The API is being built in parallel; accept the three plausible spellings
  // rather than let a casing mismatch turn a countdown into "try again later".
  const raw =
    payload.retryAfterSeconds ?? payload.retry_after_seconds ?? payload.retryAfter ?? null;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  return Math.min(Math.ceil(n), MAX_RETRY_SECONDS);
}

export function classifyLoginError(err: unknown): LoginError {
  if (!(err instanceof ApiCallError)) {
    return { kind: 'transport', message: UNKNOWN_ERROR_MESSAGE, retryAfterSeconds: null };
  }

  if (err.kind === 'timeout') {
    return { kind: 'transport', message: TIMEOUT_MESSAGE, retryAfterSeconds: null };
  }
  if (err.kind === 'network') {
    return { kind: 'transport', message: NETWORK_MESSAGE, retryAfterSeconds: null };
  }
  if (err.status >= 500) {
    return { kind: 'transport', message: SERVER_ERROR_MESSAGE, retryAfterSeconds: null };
  }

  const payload = err.payload as Record<string, unknown>;

  if (err.status === 429) {
    return {
      kind: 'throttled',
      message: 'Too many sign-in attempts. Try again in a few minutes.',
      retryAfterSeconds: retryAfterFrom(payload) ?? DEFAULT_RETRY_SECONDS,
    };
  }

  if (
    err.status === 401 &&
    (payload.error === 'account_deactivated' || payload.reason === 'account_deactivated')
  ) {
    return { kind: 'deactivated', message: DEACTIVATED_NOTICE, retryAfterSeconds: null };
  }

  if (err.status === 401 || err.status === 403) {
    return { kind: 'credentials', message: INVALID_CREDENTIALS_MESSAGE, retryAfterSeconds: null };
  }

  if (err.status === 410) {
    // MAGIC_LINK_ENABLED went false between the config fetch and the click.
    return {
      kind: 'transport',
      message: 'Sign-in links are turned off. Use your email and password.',
      retryAfterSeconds: null,
    };
  }

  // Any other 4xx. Deliberately not `err.message` — that is a machine code
  // (`invalid_body`, `bad_request`) and putting it on the login screen is how a
  // user ends up reading server internals.
  return {
    kind: 'transport',
    message: "Couldn't sign you in. Try again.",
    retryAfterSeconds: null,
  };
}

/**
 * "14 minutes" / "45 seconds" — the tail of the throttle sentence.
 *
 * Rounds UP so the countdown never tells someone to try again a beat before the
 * server will accept it, which would read as the lockout being broken.
 */
export function formatThrottleWait(secondsRemaining: number): string {
  const s = Math.ceil(secondsRemaining);
  if (!Number.isFinite(s) || s <= 0) return 'a moment';
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const minutes = Math.ceil(s / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * Errors on the set-password screens (accept-invite, reset). The token failures
 * all collapse to one message on purpose: expired, already-consumed, and
 * never-existed are the same dead end and the same fix — ask for a new link.
 */
export function classifySetPasswordError(err: unknown, kind: 'invite' | 'reset'): string {
  if (!(err instanceof ApiCallError)) return UNKNOWN_ERROR_MESSAGE;
  if (err.kind === 'timeout') return TIMEOUT_MESSAGE;
  if (err.kind === 'network') return NETWORK_MESSAGE;
  if (err.status >= 500) return SERVER_ERROR_MESSAGE;
  if (err.status === 429) return 'Too many attempts. Try again in a few minutes.';

  const payload = err.payload as Record<string, unknown>;
  if (payload.error === 'weak_password') {
    return typeof payload.message === 'string'
      ? payload.message
      : 'That password does not meet the requirements. Try a longer one.';
  }

  return kind === 'invite'
    ? 'This invitation link has expired or has already been used. Ask your project administrator to send a new one.'
    : 'This reset link has expired or has already been used. Request a new one below.';
}
